import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

export function useDebounce<T>(value: T, delay: number): [T, Dispatch<SetStateAction<T>>] {
  // On first render, immediately set the debounced value to the value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // If we have a delay, kickoff a timeout that will update
    // our value after the delay
    let handler: NodeJS.Timeout | undefined;
    if (delay > 0) {
      handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
    }

    return () => {
      // Clear out the timeout when our value (or delay) changes
      if (handler) {
        clearTimeout(handler);
      }
    };
  }, [value, delay]);

  if (delay <= 0) {
    // If we have no delay, just return the value and setter
    return [value, setDebouncedValue];
  } else {
    // If we have a delay, return the debounced value and setter
    return [debouncedValue, setDebouncedValue];
  }
}
